/**
 * Нетарифка — проверка логистом на этапе КП (куски Л1–Л3, с 25.09.2026).
 * Паспорт функции — docs/features/nontariff.md, роль — docs/roles/logist.md.
 *
 * По каждой позиции из-за рубежа логист узнаёт у брокера: нужны ли
 * разрешительные документы, ставку пошлины, повышенный сбор, «Честный знак»,
 * код ТН ВЭД. Статус проверки — одна шкала на позицию (логист один, брокер один).
 */
import type { MatrixLabel } from './matrices'
import type { Role } from './roles'

export const CustomsCheckStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  ANSWERED: 'ANSWERED',
  NEEDS_INFO: 'NEEDS_INFO',
  NOT_REQUIRED: 'NOT_REQUIRED',
} as const
export type CustomsCheckStatus = (typeof CustomsCheckStatus)[keyof typeof CustomsCheckStatus]

export const CUSTOMS_CHECK_STATUSES: CustomsCheckStatus[] = [
  'PENDING',
  'SENT',
  'ANSWERED',
  'NEEDS_INFO',
  'NOT_REQUIRED',
]

export const CUSTOMS_STATUS_LABELS_RU: Record<CustomsCheckStatus, string> = {
  PENDING: 'В очереди',
  SENT: 'Отправлено брокеру',
  ANSWERED: 'Ответ получен',
  NEEDS_INFO: 'Нужна информация',
  NOT_REQUIRED: 'Не требуется',
}

/** Вкладка «Активные» очереди: ждут действия логиста или ответа брокера. */
export const CUSTOMS_ACTIVE_STATUSES: CustomsCheckStatus[] = ['PENDING', 'SENT', 'NEEDS_INFO']
/** Вкладка «Обработано»: ответ есть или проверка не нужна. */
export const CUSTOMS_DONE_STATUSES: CustomsCheckStatus[] = ['ANSWERED', 'NOT_REQUIRED']
/**
 * Статусы, которые логист ставит вручную. «Отправлено брокеру» и «Ответ получен»
 * ставят только свои операции: файл брокеру и ввод/загрузка ответа.
 */
export const CUSTOMS_MANUAL_STATUSES: CustomsCheckStatus[] = [
  'PENDING',
  'NEEDS_INFO',
  'NOT_REQUIRED',
]

export const ChestnyZnak = {
  YES: 'YES',
  NO: 'NO',
  UNKNOWN: 'UNKNOWN',
} as const
export type ChestnyZnak = (typeof ChestnyZnak)[keyof typeof ChestnyZnak]

export const CHESTNY_ZNAK_VALUES: ChestnyZnak[] = ['YES', 'NO', 'UNKNOWN']

export const CHESTNY_ZNAK_LABELS_RU: Record<ChestnyZnak, string> = {
  YES: 'Нужен',
  NO: 'Не нужен',
  UNKNOWN: 'Неизвестно',
}

/** Откуда в позиции ответ: ручной ввод логиста или файл брокера. */
export const CustomsSource = {
  BROKER: 'BROKER',
  MANUAL: 'MANUAL',
} as const
export type CustomsSource = (typeof CustomsSource)[keyof typeof CustomsSource]

export const CUSTOMS_SOURCE_LABELS_RU: Record<CustomsSource, string> = {
  BROKER: 'Файл брокера',
  MANUAL: 'Вручную',
}

/** Запрос брокеру: один файл Excel с уникальными партномерами. */
export const BrokerRequestStatus = {
  SENT: 'SENT',
  PARTIAL: 'PARTIAL',
  ANSWERED: 'ANSWERED',
} as const
export type BrokerRequestStatus = (typeof BrokerRequestStatus)[keyof typeof BrokerRequestStatus]

export const BROKER_REQUEST_STATUS_LABELS_RU: Record<BrokerRequestStatus, string> = {
  SENT: 'Ждём ответа',
  PARTIAL: 'Ответ частично',
  ANSWERED: 'Ответ получен',
}

export const BrokerRequestItemStatus = {
  PENDING: 'PENDING',
  ANSWERED: 'ANSWERED',
} as const
export type BrokerRequestItemStatus =
  (typeof BrokerRequestItemStatus)[keyof typeof BrokerRequestItemStatus]

export const BROKER_REQUEST_ITEM_STATUS_LABELS_RU: Record<BrokerRequestItemStatus, string> = {
  PENDING: 'Ждём ответа',
  ANSWERED: 'Ответ получен',
}

/**
 * Матрицы очереди нетарифки: всё, что везут из-за рубежа, — все матрицы, кроме
 * «РФ» (покупка внутри России) и «Склада» (товар уже у нас), решение 25.09.2026.
 * Позиции без матрицы не попадают — как и в очередь квот: матрицу выбирает
 * менеджер, пока её нет, позицию не везут ни закупке, ни логисту.
 */
export const CUSTOMS_MATRICES: MatrixLabel[] = ['Китай', 'Производители', 'Стоки']

export function isCustomsMatrix(matrix: string | null | undefined): boolean {
  return !!matrix && (CUSTOMS_MATRICES as string[]).includes(matrix)
}

/**
 * Статусы позиции, при которых она выпадает из очереди нетарифки: позиция
 * отменена, неактуальна или в архиве — везти нечего. Тот же список, что у очереди квот.
 */
export const CUSTOMS_EXCLUDED_ITEM_STATUSES = ['CANCELLED', 'NOT_ACTUAL', 'ARCHIVED'] as const

/**
 * Кто видит раздел «Нетарифка»: логист, ADMIN, генеральный директор, РОЗ.
 * Правит только логист (решение Сергея 25.09.2026), остальные — просмотр.
 */
export const CUSTOMS_VIEW_ROLES: Role[] = ['LOGIST', 'ADMIN', 'GENERAL_DIRECTOR', 'ROS']
export const CUSTOMS_EDIT_ROLES: Role[] = ['LOGIST']

export function canViewCustoms(role: string | null | undefined): boolean {
  return role ? (CUSTOMS_VIEW_ROLES as string[]).includes(role) : false
}
export function canEditCustoms(role: string | null | undefined): boolean {
  return role ? (CUSTOMS_EDIT_ROLES as string[]).includes(role) : false
}

/** Повышенный таможенный сбор для электронных компонентов, ₽ — берётся на декларацию. */
export const RAISED_CUSTOMS_FEE_RUB = 73860

/** Длина кода ТН ВЭД. */
export const TNVED_CODE_LENGTH = 10

/**
 * Кто отправляет позиции логисту кнопкой «Отправить брокеру» в карточке заявки
 * (с 17.10.2026, решение Сергея): позиции попадают в очередь нетарифки только так.
 */
export const CUSTOMS_SEND_ROLES: Role[] = ['ADMIN', 'MANAGER', 'ROP', 'ASSISTANT']
export function canSendToCustoms(role: string | null | undefined): boolean {
  return role ? (CUSTOMS_SEND_ROLES as string[]).includes(role) : false
}

/** Отчёт «Нетарифка» в «Аналитике»: что менеджеры отправляли логисту. ГД — просмотр. */
export const CUSTOMS_REPORT_ROLES: Role[] = ['ADMIN', 'GENERAL_DIRECTOR', 'ROP']

/** Отметка «Нужна была отправка» в отчёте — ставят и снимают админ и РОП. */
export const CUSTOMS_MISSED_MARK_ROLES: Role[] = ['ADMIN', 'ROP']

/**
 * Подпись статуса «В очереди» у менеджера: для него позиция «Отправлено логисту»
 * (логист сам решает, когда отправить брокеру).
 */
export const CUSTOMS_SENT_TO_LOGIST_LABEL_RU = 'Отправлено логисту'
